// ═══════════════════════════════════════════════════════════════════════════
// GESTION CAROTTE - Étape 1 du Chemin du Possible
// ═══════════════════════════════════════════════════════════════════════════
// frontend/src/modules/admin/components/GestionCarotte.jsx
// Farm-Booking - Papy 73+ - Session 01/01/2026
// "Du semis à la table du fermier"
// VERSION AUTONOME + OPEN-METEO
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo } from 'react';
import {
  Sprout, Leaf, Calendar, Clock, Droplets, Thermometer,
  AlertTriangle, CheckCircle, Info, Play, RotateCcw,
  TrendingUp, Activity, Sun, CloudRain, Target, Zap
} from 'lucide-react';
import BilanHydrique from './BilanHydrique';

// ═══════════════════════════════════════════════════════════════════════════
// FICHE CAROTTE COMPLÈTE (données FAO-56 intégrées)
// ═══════════════════════════════════════════════════════════════════════════

const FICHE_CAROTTE = {
  identite: {
    code: 'CAROTTE-NANTAISE',
    nom: 'Carotte Nantaise améliorée',
    icone: '🥕',
    couleur: '#f97316',
    famille: 'Apiacées'
  },
  stades: [
    { 
      code: 'INIT', ordre: 1, nom: 'Germination', 
      duree: 25, kc: 0.70, couleur: '#22c55e',
      description: 'Du semis à 10% couverture',
      sensibilite: 'haute',
      actions: ['Maintenir humidité surface', 'Éviter croûte', 'Voile P17 si gel'],
      seuils: { hum_min: 30, hum_opt: 45, temp_sol_min: 8 }
    },
    { 
      code: 'DEV', ordre: 2, nom: 'Développement', 
      duree: 30, kc: 0.85, couleur: '#3b82f6',
      description: 'Croissance feuillage actif',
      sensibilite: 'moyenne',
      actions: ['Éclaircir à 5cm', 'Biner', 'Pailler', 'Associer poireaux'],
      seuils: { hum_min: 28, hum_opt: 40, temp_sol_min: 10 }
    },
    { 
      code: 'MID', ordre: 3, nom: 'Grossissement', 
      duree: 40, kc: 1.05, couleur: '#f59e0b',
      description: 'Phase critique - Grossissement racine',
      sensibilite: 'haute',
      actions: ['Arrosage régulier profond', 'Surveiller mouche', 'Maintenir paillage'],
      seuils: { hum_min: 25, hum_opt: 40, temp_sol_min: 12 }
    },
    { 
      code: 'FIN', ordre: 4, nom: 'Maturation', 
      duree: 20, kc: 0.95, couleur: '#8b5cf6',
      description: 'Réduire arrosage progressivement',
      sensibilite: 'faible',
      actions: ['Réduire arrosage', 'Préparer stockage', 'Récolter avant gel'],
      seuils: { hum_min: 20, hum_opt: 35, temp_sol_min: 8 }
    }
  ],
  cycle_total: 115,
  besoins: {
    temp_sol_min: 7,
    temp_sol_opt: 15,
    ph_min: 6.0,
    ph_max: 7.0
  }
};

// Parcelles de démo (pas besoin d'API)
const PARCELLES_DEMO = [
  { id: 1, nom: 'Parcelle A - Choux', surface: 50 },
  { id: 2, nom: 'Parcelle B - Courges', surface: 50 },
  { id: 3, nom: 'Parcelle C - Poireaux', surface: 50 },
  { id: 4, nom: 'Parcelle D - Carottes', surface: 50 },
  { id: 5, nom: 'Parcelle E - Jachère', surface: 50 }
];

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Jauge circulaire
// ═══════════════════════════════════════════════════════════════════════════

const GaugeCirculaire = ({ value, max, label, unit, color, icon: Icon }) => {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (pct / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-24 h-24">
        <svg className="w-24 h-24 transform -rotate-90">
          <circle cx="48" cy="48" r="40" stroke="#e5e7eb" strokeWidth="8" fill="none" />
          <circle 
            cx="48" cy="48" r="40" 
            stroke={color} strokeWidth="8" fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {Icon && <Icon className="w-5 h-5 text-gray-500" />}
          <span className="text-lg font-bold">{value}</span>
          <span className="text-xs text-gray-500">{unit}</span>
        </div>
      </div>
      <span className="text-sm font-medium text-gray-600 mt-1">{label}</span>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Barre de progression du cycle
// ═══════════════════════════════════════════════════════════════════════════

const BarreProgression = ({ stades, jourActuel, cycleTotal }) => {
  let joursAccumules = 0;

  return (
    <div className="bg-white rounded-xl p-4 shadow-lg">
      <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <Activity className="w-5 h-5 text-orange-500" />
        Progression du cycle ({jourActuel} / {cycleTotal} jours)
      </h3>
      
      <div className="relative">
        {/* Barre de fond */}
        <div className="h-8 bg-gray-100 rounded-full overflow-hidden flex">
          {stades.map((stade, idx) => {
            const pct = (stade.duree / cycleTotal) * 100;
            return (
              <div 
                key={stade.code}
                className="h-full flex items-center justify-center text-xs font-medium text-white transition-all"
                style={{ 
                  width: `${pct}%`, 
                  backgroundColor: stade.couleur,
                  opacity: joursAccumules + stade.duree < jourActuel ? 0.5 : 1
                }}
                title={`${stade.nom}: ${stade.duree} jours (Kc=${stade.kc})`}
              >
                {pct > 15 && stade.nom}
              </div>
            );
          })}
        </div>
        
        {/* Curseur position actuelle */}
        <div 
          className="absolute top-0 h-8 w-1 bg-red-600 rounded transition-all duration-300"
          style={{ left: `${Math.min(100, (jourActuel / cycleTotal) * 100)}%` }}
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-red-600 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap">
            Jour {jourActuel}
          </div>
        </div>
      </div>
      
      {/* Légende */}
      <div className="flex flex-wrap justify-center gap-3 mt-4 text-xs">
        {stades.map(stade => (
          <div key={stade.code} className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: stade.couleur }} />
            <span>{stade.nom} ({stade.duree}j)</span>
            <span className="text-gray-400">Kc={stade.kc}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Carte du stade actuel
// ═══════════════════════════════════════════════════════════════════════════

const CarteStadeActuel = ({ stade, jourDansStade }) => {
  if (!stade) return null;

  const progression = Math.min(100, (jourDansStade / stade.duree) * 100);

  return (
    <div 
      className="rounded-xl p-5 shadow-lg border-2"
      style={{ 
        backgroundColor: `${stade.couleur}10`,
        borderColor: stade.couleur 
      }}
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">🥕</span>
            <h3 className="text-xl font-bold" style={{ color: stade.couleur }}>
              {stade.nom}
            </h3>
            <span className="bg-white px-2 py-0.5 rounded text-sm font-mono">
              Kc = {stade.kc}
            </span>
          </div>
          <p className="text-gray-600 mt-1">{stade.description}</p>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
          stade.sensibilite === 'haute' ? 'bg-red-100 text-red-700' :
          stade.sensibilite === 'moyenne' ? 'bg-yellow-100 text-yellow-700' :
          'bg-green-100 text-green-700'
        }`}>
          Sensibilité {stade.sensibilite}
        </div>
      </div>

      {/* Progression dans le stade */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>Jour {jourDansStade} / {stade.duree}</span>
          <span>{Math.round(progression)}%</span>
        </div>
        <div className="h-3 bg-white rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${progression}%`, backgroundColor: stade.couleur }}
          />
        </div>
      </div>

      {/* Seuils */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-white rounded-lg p-3 text-center">
          <Droplets className="w-5 h-5 mx-auto text-blue-500 mb-1" />
          <div className="text-lg font-bold">{stade.seuils.hum_min}%</div>
          <div className="text-xs text-gray-500">Humidité min</div>
        </div>
        <div className="bg-white rounded-lg p-3 text-center">
          <Target className="w-5 h-5 mx-auto text-green-500 mb-1" />
          <div className="text-lg font-bold">{stade.seuils.hum_opt}%</div>
          <div className="text-xs text-gray-500">Humidité opt</div>
        </div>
        <div className="bg-white rounded-lg p-3 text-center">
          <Thermometer className="w-5 h-5 mx-auto text-orange-500 mb-1" />
          <div className="text-lg font-bold">{stade.seuils.temp_sol_min}°C</div>
          <div className="text-xs text-gray-500">T° sol min</div>
        </div>
      </div>

      {/* Actions */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">📋 Actions recommandées :</h4>
        <div className="flex flex-wrap gap-2">
          {stade.actions.map((action, idx) => (
            <span 
              key={idx}
              className="bg-white px-3 py-1 rounded-full text-sm border"
              style={{ borderColor: stade.couleur }}
            >
              {action}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Simulateur de plantation
// ═══════════════════════════════════════════════════════════════════════════

const SimulateurPlantation = ({ onSimuler }) => {
  const [dateSemis, setDateSemis] = useState('');
  const [parcelle, setParcelle] = useState('');

  const handleSimuler = () => {
    if (dateSemis) {
      onSimuler({ dateSemis, parcelle });
    }
  };

  // Date par défaut : aujourd'hui
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-5 shadow-lg border border-orange-200">
      <h3 className="font-bold text-orange-800 mb-4 flex items-center gap-2">
        <Play className="w-5 h-5" />
        🥕 Simuler une plantation de carottes
      </h3>
      
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date de semis
          </label>
          <input 
            type="date" 
            value={dateSemis || today}
            onChange={(e) => setDateSemis(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Parcelle
          </label>
          <select 
            value={parcelle}
            onChange={(e) => setParcelle(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
          >
            <option value="">-- Sélectionner --</option>
            {PARCELLES_DEMO.map(p => (
              <option key={p.id} value={p.id}>{p.nom} ({p.surface}m²)</option>
            ))}
          </select>
        </div>
        
        <div className="flex items-end">
          <button 
            onClick={handleSimuler}
            disabled={!dateSemis}
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Sprout className="w-5 h-5" />
            Simuler le cycle
          </button>
        </div>
      </div>
      
      <div className="mt-4 p-3 bg-white rounded-lg text-sm text-gray-600">
        <Info className="w-4 h-4 inline mr-1" />
        La simulation calcule les stades, dates prévisionnelles et coefficients Kc selon les données FAO-56.
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Timeline prévisionnelle
// ═══════════════════════════════════════════════════════════════════════════

const TimelinePrevisionnelle = ({ dateSemis, stades }) => {
  if (!dateSemis) return null;

  const dateDebut = new Date(dateSemis);
  let dateAccumulee = new Date(dateDebut);

  const timeline = stades.map(stade => {
    const debut = new Date(dateAccumulee);
    dateAccumulee.setDate(dateAccumulee.getDate() + stade.duree);
    const fin = new Date(dateAccumulee);
    return { ...stade, dateDebut: debut, dateFin: fin };
  });

  const dateRecolte = timeline[timeline.length - 1].dateFin;

  return (
    <div className="bg-white rounded-xl p-5 shadow-lg">
      <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
        <Calendar className="w-5 h-5 text-blue-600" />
        📅 Timeline prévisionnelle
      </h3>

      <div className="space-y-3">
        {timeline.map((stade, idx) => (
          <div key={stade.code} className="flex items-center gap-4">
            <div 
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: stade.couleur }}
            />
            <div className="flex-1">
              <div className="flex justify-between">
                <span className="font-medium">{stade.nom}</span>
                <span className="text-gray-500 text-sm">
                  Kc = {stade.kc}
                </span>
              </div>
              <div className="text-sm text-gray-600">
                {stade.dateDebut.toLocaleDateString('fr-FR')} → {stade.dateFin.toLocaleDateString('fr-FR')}
                <span className="text-gray-400 ml-2">({stade.duree} jours)</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
        <div className="flex items-center gap-2 text-green-800 font-semibold">
          <CheckCircle className="w-5 h-5" />
          🥕 Récolte prévue : {dateRecolte.toLocaleDateString('fr-FR', { 
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' 
          })}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Calcul ETc
// ═══════════════════════════════════════════════════════════════════════════

const CalculETc = ({ stade, et0 = 4.5 }) => {
  if (!stade) return null;

  const etc = (et0 * stade.kc).toFixed(2);

  return (
    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-5 shadow-lg border border-blue-200">
      <h3 className="font-bold text-blue-800 mb-4 flex items-center gap-2">
        <Droplets className="w-5 h-5" />
        💧 Calcul des besoins en eau (ETc)
      </h3>

      <div className="grid grid-cols-3 gap-4 text-center">
        <div className="bg-white rounded-lg p-4">
          <div className="text-3xl font-bold text-blue-600">{et0}</div>
          <div className="text-sm text-gray-600">ET₀ (mm/jour)</div>
          <div className="text-xs text-gray-400">Évapotranspiration référence</div>
        </div>
        
        <div className="bg-white rounded-lg p-4 flex items-center justify-center">
          <div>
            <div className="text-2xl font-bold text-gray-600">×</div>
            <div className="text-3xl font-bold" style={{ color: stade.couleur }}>{stade.kc}</div>
            <div className="text-sm text-gray-600">Kc ({stade.nom})</div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-4">
          <div className="text-3xl font-bold text-green-600">{etc}</div>
          <div className="text-sm text-gray-600">ETc (mm/jour)</div>
          <div className="text-xs text-gray-400">Besoin culture</div>
        </div>
      </div>

      <div className="mt-4 p-3 bg-white rounded-lg">
        <div className="text-sm">
          <strong>📊 Interprétation :</strong> Avec un Kc de {stade.kc} en phase "{stade.nom}", 
          la carotte a besoin de <strong>{etc} mm/jour</strong> d'eau.
          <br />
          Sur une semaine : <strong>{(etc * 7).toFixed(1)} mm</strong>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL : GestionCarotte
// ═══════════════════════════════════════════════════════════════════════════

const GestionCarotte = () => {
  const [simulation, setSimulation] = useState(null);
  const [jourSimule, setJourSimule] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [ongletActif, setOngletActif] = useState('simulateur'); // 'simulateur' ou 'hydrique'

  // Calculer le stade actuel basé sur le jour
  const stadeActuel = useMemo(() => {
    let joursAccumules = 0;
    for (const stade of FICHE_CAROTTE.stades) {
      if (jourSimule <= joursAccumules + stade.duree) {
        return {
          ...stade,
          jourDansStade: jourSimule - joursAccumules
        };
      }
      joursAccumules += stade.duree;
    }
    return { ...FICHE_CAROTTE.stades[FICHE_CAROTTE.stades.length - 1], jourDansStade: 20 };
  }, [jourSimule]);

  // Animation play/pause
  useEffect(() => {
    let interval;
    if (isPlaying && jourSimule < FICHE_CAROTTE.cycle_total) {
      interval = setInterval(() => {
        setJourSimule(j => Math.min(j + 1, FICHE_CAROTTE.cycle_total));
      }, 200);
    }
    return () => clearInterval(interval);
  }, [isPlaying, jourSimule]);

  // Lancer simulation
  const handleSimuler = ({ dateSemis, parcelle }) => {
    setSimulation({ dateSemis, parcelle });
    setJourSimule(1);
    setIsPlaying(false);
  };

  // Reset
  const handleReset = () => {
    setJourSimule(0);
    setIsPlaying(false);
    setSimulation(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-amber-50 p-4 md:p-6">
      {/* HEADER */}
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold text-orange-800 flex items-center justify-center gap-3">
          <span className="text-4xl">🥕</span>
          Gestion Carotte
          <span className="text-lg font-normal text-orange-600">FAO-56</span>
        </h1>
        <p className="text-gray-600 mt-1">
          Du semis à la table du fermier - L'eau juste, pas une goutte de perdue 💧
        </p>
      </div>

      {/* ONGLETS */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex gap-2 bg-white rounded-lg p-1 shadow">
          <button
            onClick={() => setOngletActif('simulateur')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-all ${
              ongletActif === 'simulateur'
                ? 'bg-orange-500 text-white shadow-lg'
                : 'text-gray-600 hover:bg-orange-50'
            }`}
          >
            <Sprout className="w-5 h-5" />
            🥕 Simulateur Culture
          </button>
          <button
            onClick={() => setOngletActif('hydrique')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-all ${
              ongletActif === 'hydrique'
                ? 'bg-blue-500 text-white shadow-lg'
                : 'text-gray-600 hover:bg-blue-50'
            }`}
          >
            <Droplets className="w-5 h-5" />
            💧 Bilan Hydrique
          </button>
        </div>
      </div>

      {/* CONTENU ONGLET SIMULATEUR */}
      {ongletActif === 'simulateur' && (
        <div className="max-w-6xl mx-auto space-y-6">
          <SimulateurPlantation onSimuler={handleSimuler} />

          {/* SI SIMULATION ACTIVE */}
          {simulation && (
            <>
              {/* Contrôles */}
              <div className="flex justify-center gap-4">
                <button 
                  onClick={() => setIsPlaying(!isPlaying)}
                  className={`px-6 py-2 rounded-lg font-semibold flex items-center gap-2 ${
                    isPlaying 
                      ? 'bg-yellow-500 hover:bg-yellow-600 text-white' 
                      : 'bg-green-500 hover:bg-green-600 text-white'
                  }`}
                >
                  {isPlaying ? <Clock className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  {isPlaying ? 'Pause' : 'Lecture'}
                </button>
                <button 
                  onClick={handleReset}
                  className="px-6 py-2 rounded-lg font-semibold bg-gray-500 hover:bg-gray-600 text-white flex items-center gap-2"
                >
                  <RotateCcw className="w-5 h-5" />
                  Reset
                </button>
                <input 
                  type="range" 
                  min="0" 
                  max={FICHE_CAROTTE.cycle_total}
                  value={jourSimule}
                  onChange={(e) => setJourSimule(parseInt(e.target.value))}
                  className="w-48"
                />
              </div>

              {/* Barre de progression */}
              <BarreProgression 
                stades={FICHE_CAROTTE.stades} 
                jourActuel={jourSimule}
                cycleTotal={FICHE_CAROTTE.cycle_total}
              />

            {/* Grille principale */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Carte stade actuel */}
              <CarteStadeActuel 
                stade={stadeActuel} 
                jourDansStade={stadeActuel.jourDansStade}
              />

              {/* Calcul ETc */}
              <CalculETc stade={stadeActuel} et0={4.5} />
            </div>

            {/* Timeline */}
            <TimelinePrevisionnelle 
              dateSemis={simulation.dateSemis}
              stades={FICHE_CAROTTE.stades}
            />
          </>
        )}

        {/* INFO SI PAS DE SIMULATION */}
        {!simulation && (
          <div className="bg-white rounded-xl p-8 shadow-lg text-center">
            <Sprout className="w-16 h-16 mx-auto text-orange-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">
              Prêt à simuler une plantation de carottes ?
            </h2>
            <p className="text-gray-500 max-w-md mx-auto">
              Sélectionnez une date de semis ci-dessus pour visualiser le cycle complet 
              avec les coefficients Kc FAO-56, les stades et les actions recommandées.
            </p>

            {/* Résumé des stades */}
            <div className="mt-6 grid grid-cols-4 gap-3">
              {FICHE_CAROTTE.stades.map(stade => (
                <div 
                  key={stade.code}
                  className="p-3 rounded-lg border"
                  style={{ borderColor: stade.couleur, backgroundColor: `${stade.couleur}10` }}
                >
                  <div className="font-bold" style={{ color: stade.couleur }}>{stade.nom}</div>
                  <div className="text-sm text-gray-600">{stade.duree} jours</div>
                  <div className="text-lg font-mono">Kc={stade.kc}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        </div>
      )}

      {/* CONTENU ONGLET BILAN HYDRIQUE */}
      {ongletActif === 'hydrique' && (
        <div className="max-w-6xl mx-auto">
          <BilanHydrique 
            dateSemis={simulation?.dateSemis}
            stades={FICHE_CAROTTE.stades}
            stadeActuel={stadeActuel}
            jourCulture={jourSimule}
          />
        </div>
      )}

      {/* FOOTER */}
      <div className="text-center text-sm text-gray-500 py-4 mt-6">
        🥕 Gestion Carotte - Farm-Booking - Papy 73+ - Session 01/01/2026
        <br />
        <span className="text-xs">Données FAO-56 + Open-Meteo | "L'eau juste, pas une goutte de perdue" 💧</span>
      </div>
    </div>
  );
};

export default GestionCarotte;